
// Enhanced i18n methods
import i18n from 'i18next';
import { supabase } from '@/integrations/supabase/client';
import { loadTranslationsFromDb } from './utils';

/**
 * Extends i18n with custom methods for managing translations
 */
export const extendI18n = () => {
  /**
   * Reloads resources from database
   */
  i18n.reloadResourcesFromDb = async function() {
    const dbResources = await loadTranslationsFromDb();
    
    if (dbResources && i18n.options) {
      i18n.options.resources = dbResources;
      
      localStorage.setItem('i18nextResources', JSON.stringify(dbResources));
      
      await i18n.reloadResources();
      
      const currentLang = i18n.language;
      await i18n.changeLanguage(currentLang);
      
      const lastUpdate = window.__lastTranslationUpdate || 0;
      const now = Date.now();
      
      if (now - lastUpdate > 200) {
        window.__lastTranslationUpdate = now;
        document.dispatchEvent(new CustomEvent('translationsUpdated'));
      }
      
      return Promise.resolve();
    } else {
      return Promise.resolve();
    }
  };

  /**
   * Saves a translation to the database
   */
  i18n.saveTranslation = async function(locale: string, key: string, value: string) {
    try {
      const { error } = await supabase.rpc('upsert_translation', {
        p_locale: locale,
        p_key: key,
        p_value: value
      });
      
      if (error) {
        console.error("Error saving translation to database:", error);
        return false;
      }
      
      if (i18n.options && i18n.options.resources) {
        if (!i18n.options.resources[locale]) {
          i18n.options.resources[locale] = { common: {} };
        }
        
        const parts = key.split('.');
        let current = i18n.options.resources[locale].common;
        
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          }
          current = current[parts[i]];
        }
        
        current[parts[parts.length - 1]] = value;
        
        localStorage.setItem('i18nextResources', JSON.stringify(i18n.options.resources));
        
        await i18n.reloadResources();
        
        const lastUpdate = window.__lastTranslationUpdate || 0;
        const now = Date.now();
        
        if (now - lastUpdate > 200) {
          window.__lastTranslationUpdate = now;
          document.dispatchEvent(new CustomEvent('translationsUpdated'));
        }
        
        return true;
      }
      
      return true;
    } catch (error) {
      console.error("Exception saving translation to database:", error);
      return false;
    }
  };
};

/**
 * Sets up storage event listeners for cross-tab translation syncing
 */
export const setupStorageListeners = () => {
  window.addEventListener('storage', (event) => {
    if (event.key === 'i18nextResources' && event.newValue) {
      try {
        const resources = JSON.parse(event.newValue);
        if (i18n.options && resources) {
          i18n.options.resources = resources;
          i18n.reloadResources().then(() => {
            console.log("Translations updated from another tab");
            document.dispatchEvent(new CustomEvent('translationsUpdated'));
          });
        }
      } catch (error) {
        console.error("Error processing storage event for translations:", error);
      }
    }
  });
};
